import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { translate } from '../../../Services/translation';
import {
  ExtapiInvoice,
  InvoiceRow,
} from '../../../Types/MyPages/InvoicesModel';
import clsx from 'clsx';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getInvoiceHead } from '../../../Actions/Invoices.action';
import ArrowSquareOutIcon from '../../../../icons/arrow-square-out.svg';
import Icon from '../../Icon';

interface GroupedInvoiceRows {
  [parcelNumber: string]: InvoiceRow[];
}

export default function MyPageInvoiceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState<ExtapiInvoice>();
  const [invoiceRows, setInvoiceRows] = useState<GroupedInvoiceRows>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const fetchInvoice = async () => {
    setIsLoading(true);
    setErrorMessage(undefined);
    try {
      const data = await getInvoiceHead(id);
      setInvoice(data.invoice);
      setInvoiceRows(data.invoiceRows);
    } catch (error) {
      if (error.response.status == 404) {
        setErrorMessage(translate('mypage.invoices.filter-no-result'));
      } else {
        setErrorMessage(translate('general.error'));
      }
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, []);

  const isCredit = useMemo(() => {
    return invoice?.documentType == 'Credit Memo';
  }, [invoice?.documentType]);

  return (
    <div className="col-span-3 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <button
          className="btn btn--clear flex items-center"
          onClick={() => navigate(-1)}
        >
          <Icon name="chevron-left" size="lg" />
          {translate('general.back')}
        </button>
      </div>

      <div className="align-center flex justify-between">
        {invoice?.documentNo && (
          <h3>
            {translate('mypage.invoices.invoicenumber')}:{' '}
            {invoice?.documentNo}
          </h3>
        )}
        {/* <button className='btn btn--outline p-2'>{translate("mypage.invoices.kolli")}</button> */}
      </div>
      {/* Mobile */}
      <ul className="max-h-[648px] overflow-y-auto lg:hidden">
        {errorMessage && (
          <small className="text-error-500 lg:hidden">
            {errorMessage}
          </small>
        )}
        {!isLoading &&
          invoiceRows &&
          Object.keys(invoiceRows)?.map((parcelNumber, idx) => (
            <li
              key={`invoice-group-mobile-${parcelNumber}-${idx}`}
              className="mb-4 rounded border border-solid border-gray-300 bg-white p-4"
            >
              <h4 className="mb-2">
                <ParcelTitle
                  parcelNumber={parcelNumber}
                  trackingUrl={invoiceRows[parcelNumber]![0]?.trackingUrl}
                />
              </h4>
              <ul>
                {invoiceRows[parcelNumber]!.map((row, idx) => (
                  <li
                    key={`invoice-mobile-${row.id}-${idx}`}
                    className="mb-2"
                  >
                    <div className="mb-1 flex justify-between">
                      <p className="text-md font-bold">
                        {translate('mypage.invoices.articlenumber')}:{' '}
                        {row.articleNumber}
                      </p>
                    </div>
                    <div
                      className={clsx(
                        'grid grid-cols-2 gap-2',
                        isCredit ? 'md:grid-cols-3' : 'md:grid-cols-5'
                      )}
                    >
                      <div className="flex flex-col">
                        <p className="text-sm uppercase text-gray-600">
                          {translate('mypage.invoices.productname')}
                        </p>
                        <p className="text-md">{row.description}</p>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-sm uppercase text-gray-600">
                          {translate('mypage.invoices.invoicequantity')}
                        </p>
                        <p className="text-md">{row.invoicedQty}</p>
                      </div>
                      {!isCredit && (
                        <div className="flex flex-col">
                          <p className="text-sm uppercase text-gray-600">
                            {translate('mypage.invoices.unitprice')}
                          </p>
                          <p className="text-md">{row.unitPrice}</p>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <p className="text-sm uppercase text-gray-600">
                          {translate('mypage.invoices.lineprice')}
                        </p>
                        <p className="text-md">{row.lineAmount}</p>
                      </div>
                      {!isCredit && (
                        <div className="flex flex-col">
                          <p className="text-sm uppercase text-gray-600">
                            {translate('mypage.invoices.deliverydate')}
                          </p>
                          <p className="text-md">{row.postingDate}</p>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        {isLoading &&
          Array.from({ length: 3 }).map((_, idx) => (
            <li
              key={`invoice-mobile-skeleton-${idx}`}
              className="mb-4 flex flex-col gap-2 rounded border border-solid border-gray-300 bg-white p-4"
            >
              <span className="skeleton max-w-[40%] rounded-sm bg-gray-200">
                &nbsp;
              </span>
              <div className="grid grid-cols-2 md:grid-cols-5">
                <div className="flex max-w-[40%] flex-col gap-[2px]">
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                </div>
                <div className="flex max-w-[40%] flex-col gap-[2px]">
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                </div>
                <div className="flex max-w-[40%] flex-col gap-[2px]">
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                </div>
                <div className="flex max-w-[40%] flex-col gap-[2px]">
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                </div>
              </div>
            </li>
          ))}
      </ul>
      {/* Desktop */}
      <ul className="hidden max-h-[648px] overflow-y-auto rounded border border-solid border-gray-300 bg-white lg:block">
        <li
          className={clsx(
            'grid gap-4 rounded-t border-b border-solid border-gray-300 p-2 text-sm font-bold',
            isCredit ? 'grid-cols-6' : 'grid-cols-9'
          )}
        >
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-left">
            {translate('mypage.invoices.articlenumber')}
          </p>
          <p className="col-span-3 overflow-hidden text-ellipsis whitespace-nowrap text-left">
            {translate('mypage.invoices.productname')}
          </p>
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-left">
            {translate('mypage.invoices.invoicequantity')}
          </p>
          {!isCredit && (
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-right">
              {translate('mypage.invoices.unitprice')}
            </p>
          )}
          <p className="overflow-hidden text-ellipsis whitespace-nowrap text-right">
            {translate('mypage.invoices.lineprice')}
          </p>
          {!isCredit && (
            <p className="col-span-2 overflow-hidden text-ellipsis whitespace-nowrap text-right">
              {translate('mypage.invoices.deliverydate')}
            </p>
          )}
        </li>
        {!isLoading &&
          invoiceRows &&
          Object.keys(invoiceRows)?.map((parcelNumber, idx) => (
            <Fragment key={`invoice-group-${parcelNumber}-${idx}`}>
              <li className="grid grid-cols-1 gap-4 border border-solid border-gray-300 p-2 text-sm [&:last-child]:rounded-b [&:last-child]:border-b-0 [&:nth-child(even)]:bg-primary-50 [&:nth-child(odd)]:bg-white">
                <h6 className="text-md">
                  <ParcelTitle
                    parcelNumber={parcelNumber}
                    trackingUrl={
                      invoiceRows[parcelNumber]![0]?.trackingUrl
                    }
                  />
                </h6>
              </li>
              {invoiceRows[parcelNumber]!.map((row, idx) => (
                <li
                  key={`invoice-row-${row.id}-${idx}`}
                  className={clsx(
                    'grid gap-4 border border-solid border-gray-300 p-2 text-sm [&:last-child]:rounded-b [&:last-child]:border-b-0 [&:nth-child(even)]:bg-primary-50 [&:nth-child(odd)]:bg-white',
                    isCredit ? 'grid-cols-6' : 'grid-cols-9'
                  )}
                >
                  <p className="text-left">{row.articleNumber}</p>
                  <p className="col-span-3 text-left">{row.description}</p>
                  <p className="text-left">{row.invoicedQty}</p>
                  {!isCredit && (
                    <p className="text-right">{row.unitPrice}</p>
                  )}
                  <p className="text-right">{row.lineAmount}</p>
                  {!isCredit && (
                    <p className="col-span-2 text-right">
                      {row.postingDate}
                    </p>
                  )}
                </li>
              ))}
            </Fragment>
          ))}
        {isLoading &&
          Array.from({ length: 5 }).map((_, idx) => (
            <li
              key={'invoice-skeleton-' + idx}
              className="skeleton grid grid-cols-1 gap-4 border-b border-solid border-gray-300 bg-white px-2 py-3 [&_:last-child]:border-b-0"
            >
              <div className="skeleton bg-gray-100">&nbsp;</div>
            </li>
          ))}
      </ul>
      {errorMessage && (
        <small className="hidden text-error-500 lg:block">
          {errorMessage}
        </small>
      )}
    </div>
  );
}

const ParcelTitle = ({
  parcelNumber,
  trackingUrl,
}: {
  parcelNumber: string;
  trackingUrl?: string;
}) => {
  const title = useMemo(
    () =>
      parcelNumber == 'no_parcel' ? (
        translate('mypage.invoice.no-parcel-number')
      ) : (
        <>
          {translate('mypage.invoice.parcel')} {parcelNumber}
        </>
      ),
    [parcelNumber]
  );

  return (
    <>
      {trackingUrl && (
        <a
          href={trackingUrl}
          className="flex items-center gap-1 underline"
          target="_blank"
          rel="noreferrer"
        >
          {title} <ArrowSquareOutIcon className="h-6 w-6 lg:h-5 lg:w-5" />
        </a>
      )}
      {!trackingUrl && title}
    </>
  );
};
